import React, { useEffect, useState } from "react";
import {
  PregnancyDurationType,
  VacationCalculation as VacationData,
} from "../types/allowance";
import Calendar from "./Calendar";
import { get, post } from "../utils/apiClient";

// 假期数据类型（API返回格式）
interface HolidayItem {
  date: string; // yyyy-MM-dd
  name: string; // 原文名称
  name_cn: string; // 中文名
  name_en: string; // 英文名
  type: string; // 类型
}

// Calendar组件期望的假期数据格式
interface HolidayInfo {
  date: string; // YYYY-MM-DD format
  name: string;
  type: "holiday" | "workday"; // holiday: 公共假期, workday: 调休工作日
}

// City 类型，遵循团队数据结构规范
interface City {
  id: string;
  name: string;
  code: string;
  chineseName: string;
  englishName: string;
  province: string;
  countryCode: string;
  enabled: boolean;
  sortOrder: number;
  remark?: Record<string, unknown> | null;
  createDate: string;
  createBy: string;
  updateDate: string;
  updateBy: string;
}

interface VacationCalculationProps {
  data: VacationData;
  onChange: (data: VacationData) => void;
  expanded: boolean;
  onToggle: () => void;
  fromMaternityCalculator?: boolean; // 标识是否来自产假计算页面
}

// 后端返回的计算结果类型
interface ApiCalculateResponse {
  totalDays?: number;
  total?: number;
  totalAllowanceDays?: number; // 享受津贴天数
  baseDays?: number; // 国家法定产假
  extendedDays?: number; // 地方奖励假
  dystociaDays?: number; // 难产假
  multiBabyDays?: number; // 多胞胎假
  breakdown?: {
    baseDays?: number;
    additionalDays?: number;
    bonusDays?: number;
    difficultBirthDays?: number;
    multipleBirthDays?: number;
  };
  additionalDays?: number;
  bonusDays?: number;
  difficultBirthDays?: number;
  multipleBirthDays?: number;
  maternityPeriod?: { startDate?: string; endDate?: string };
  startDate?: string;
  endDate?: string;
  maternityStartDate?: string;
  maternityEndDate?: string;
  isFirstTimeBirth?: boolean; // 是否为一孩（绍兴城市返回）
  allowanceCompensationDetails?: string | string[] | Record<string, unknown>;
  details?: string | string[] | Record<string, unknown>;
  calculationDetail?: string | string[] | Record<string, unknown>;
}

const VacationCalculation: React.FC<VacationCalculationProps> = ({
  data,
  onChange,
  expanded,
  onToggle,
  fromMaternityCalculator = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    data.startDate ? new Date(data.startDate) : null,
  );
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Initialize default values if not provided
  useEffect(() => {
    let needsUpdate = false;
    const updatedData = { ...data };

    // 流产时设置默认孕期类型
    if (data.deliveryMethod === "miscarriage" && !data.pregnancyDurationType) {
      updatedData.pregnancyDurationType = PregnancyDurationType.WEEKS;
      needsUpdate = true;
    }

    // 绍兴城市且firstChild未设置时，默认为"yes"
    if (data.city === "SX" && !data.firstChild && data.deliveryMethod !== "miscarriage") {
      updatedData.firstChild = "yes";
      needsUpdate = true;
    }

    if (needsUpdate) {
      onChange(updatedData);
    }
  }, [data.city, data.deliveryMethod, data.pregnancyDurationType, data.firstChild, onChange]);

  const handleChange = (
    field: keyof VacationData,
    value: string | boolean | number | undefined,
  ) => {
    const criticalFields = [
      "city",
      "startDate",
      "deliveryMethod",
      "birthType",
      "hasExtendedDays",
      "pregnancyDuration",
    ];

    const newData = { ...data };

    // Only update if value is defined, otherwise remove the field
    if (value !== undefined) {
      (newData[field] as string | number | boolean) = value;
    } else {
      delete newData[field as keyof typeof newData];
    }

    // 如果修改的是关键参数且有计算结果，则清除结果
    if (criticalFields.includes(field) && data.calculatedDays > 0) {
      newData.calculatedDays = 0;
    }

    // 绍兴城市特殊处理：如果选择绍兴且firstChild未设置，默认为"yes"
    if (field === "city" && value === "SX" && !newData.firstChild) {
      newData.firstChild = "yes";
    }

    onChange(newData);

    // 清除该字段的验证错误
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // 员工工号实时验证
    if (field === "employeeId") {
      validateEmployeeId(value as string);
    }
  };

  // 员工工号验证函数
  const validateEmployeeId = (employeeId: string) => {
    const employeeIdPattern = /^[Aa]\d{7}$/;

    if (employeeId && !employeeIdPattern.test(employeeId)) {
      setValidationErrors((prev) => ({
        ...prev,
        employeeId: "员工工号格式错误，应为A5开头加6位数字（如：A5123456）",
      }));
      return false;
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.employeeId;
        return newErrors;
      });
      return true;
    }
  };

  // Update selectedDate when data.startDate changes
  useEffect(() => {
    if (data.startDate) {
      setSelectedDate(new Date(data.startDate));
    }
  }, [data.startDate]);
  const [cities, setCities] = useState<City[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [holidays, setHolidays] = useState<HolidayInfo[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  // 流产规则（与 MaternityCalculator 保持一致）
  const [miscarriageRules, setMiscarriageRules] = useState<Array<Record<string, unknown>>>([]);
  const [miscarriageRulesLoading, setMiscarriageRulesLoading] = useState(false);
  const [miscarriageRule, setMiscarriageRule] = useState<string>("");
  const [miscarriageFirstValue, setMiscarriageFirstValue] = useState<string>("");
  const [doctorRestDays, setDoctorRestDays] = useState<string>("");

  const cityOptions = cities.map((city) => ({
    value: city.code,
    label: city.chineseName || city.name,
  }));

  // 将API返回的HolidayItem转换为Calendar组件期望的HolidayInfo格式
  const convertToHolidayInfo = (holidays: HolidayItem[]): HolidayInfo[] => {
    return holidays.map((holiday) => ({
      date: holiday.date,
      name: holiday.name_cn,
      type:
        holiday.type === "public_holiday"
          ? ("holiday" as const)
          : ("workday" as const),
    }));
  };

  // 获取假期数据
  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const years = [currentYear - 2, currentYear - 1, currentYear]; // 去年、今年

      const holidayPromises = years.map((year) =>
        get<HolidayItem[]>(`https://www.54cpa.cn/api/support/holidays/${year}`),
      );

      const holidayResults = await Promise.all(holidayPromises);
      const allHolidays = holidayResults.flat();
      const convertedHolidays = convertToHolidayInfo(allHolidays);

      setHolidays(convertedHolidays);
    } catch (error) {
      console.error("获取假期数据失败:", error);
      // 失败时使用空数组，Calendar组件会正常工作
      setHolidays([]);
    } finally {
      setHolidaysLoading(false);
    }
  };

  // 获取城市数据
  const fetchCities = async () => {
    setCitiesLoading(true);
    try {
      // 接口返回 { code, message, data }，apiClient 已抽取 data
      const cityData = await get<City[]>(
        "https://www.54cpa.cn/api/support/cities",
      );
      // 按 sortOrder 升序（不按 enabled 过滤，避免后端标识差异导致全部被过滤）
      const sorted = (cityData || [])
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCities(sorted);

      // 若当前 city 不在返回列表中，则设置为第一个可用城市
      if (
        sorted.length > 0 &&
        data.city &&
        !sorted.some((c) => c.code === data.city)
      ) {
        handleChange("city", sorted[0].code);
      }
    } catch (error) {
      console.error("获取城市数据失败:", error);
    } finally {
      setCitiesLoading(false);
    }
  };

  // 组件加载时获取城市数据和假期数据
  useEffect(() => {
    if (cities.length === 0 && !citiesLoading) {
      fetchCities();
    }
    if (holidays.length === 0 && !holidaysLoading) {
      fetchHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当选择“流产”且存在城市代码时，获取对应的流产规则
  useEffect(() => {
    const isMis = data.deliveryMethod === "miscarriage";
    if (!isMis || !data.city) {
      setMiscarriageRules([]);
      setMiscarriageRule("");
      setMiscarriageFirstValue("");
      setDoctorRestDays("");
      return;
    }
    const fetchRules = async () => {
      setMiscarriageRulesLoading(true);
      try {
        const url = `https://www.54cpa.cn/api/maternity-leave/ref-data/miscarriage-rules?cityCode=${encodeURIComponent(
          data.city,
        )}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const arr = Array.isArray(json)
          ? json
          : (Array.isArray((json && (json.data as unknown)))
              ? (json.data as unknown as Array<Record<string, unknown>>)
              : []);
        setMiscarriageRules(arr as Array<Record<string, unknown>>);
        if (arr && arr.length > 0) {
          const first = arr[0] as Record<string, unknown>;
          const val = String(first["code"] ?? first["id"] ?? first["value"] ?? first["description"] ?? "");
          setMiscarriageFirstValue(val);
          setMiscarriageRule(val);
        } else {
          setMiscarriageRule("");
          setMiscarriageFirstValue("");
        }
      } catch (e) {
        console.error("获取流产规则失败:", e);
        setMiscarriageRules([]);
        setMiscarriageRule("");
        setMiscarriageFirstValue("");
      } finally {
        setMiscarriageRulesLoading(false);
      }
    };
    fetchRules();
  }, [data.deliveryMethod, data.city]);

  // Sync selectedDate when data.startDate changes (from maternity calculator)
  useEffect(() => {
    if (
      data.startDate &&
      data.startDate !== selectedDate?.toISOString().split("T")[0]
    ) {
      setSelectedDate(new Date(data.startDate));
    }
  }, [data.startDate, selectedDate]);

  // 计算假期天数
  const calculateVacationAPI = async () => {
    if (
      !data.employeeName ||
      !data.employeeId ||
      !data.city ||
      !data.startDate ||
      !data.birthType ||
      !data.deliveryMethod
    ) {
      setErrorMessage("请完善所有必填信息");
      return;
    }
    setCalculating(true);
    setErrorMessage("");

    try {
      // 找到对应的城市代码
      const selectedCity = cities.find((city) => city.code === data.city);
      if (!selectedCity) {
        setErrorMessage("请选择有效的城市");
        return;
      }

      // 构造符合后端要求的请求体，按照 MaternityCalculator 的逻辑
      const isMiscarriage = data.deliveryMethod === "miscarriage";

      const valueOf = (item: Record<string, unknown>, idx: number) =>
        String(item["code"] ?? item["id"] ?? item["value"] ?? item["description"] ?? `rule_${idx}`);

      const computeMiscarriageDetail = () => {
        if (!isMiscarriage) return undefined;
        let index = 1;
        for (let i = 0; i < miscarriageRules.length; i++) {
          const v = valueOf(miscarriageRules[i] as Record<string, unknown>, i);
          if (v === miscarriageRule) { index = i + 1; break; }
        }
        let days = 0;
        if (data.city === 'SZ' && miscarriageRule === miscarriageFirstValue) {
          const n = parseInt(doctorRestDays || '0', 10);
          days = Number.isFinite(n) && n >= 0 ? n : 0;
        } else {
          const selIndex = Math.max(0, index - 1);
          const item = miscarriageRules[selIndex] as Record<string, unknown> | undefined;
          const rawDays = item && (item['days'] ?? item['day'] ?? item['valueDays']);
          if (typeof rawDays === 'number') days = rawDays as number;
          else if (typeof rawDays === 'string') {
            const n = parseInt(rawDays as string, 10); days = Number.isFinite(n) ? n : 0;
          } else {
            const desc = item && (item['description'] ?? item['name']);
            if (typeof desc === 'string') {
              const m = (desc as string).match(/(\d{1,3})/); if (m) { const n = parseInt(m[1], 10); days = Number.isFinite(n) ? n : 0; }
            }
          }
        }
        return { cityCode: data.city, index, days };
      };

      const payload = {
        lanId: data.employeeId || "",
        employeeName: data.employeeName || "",
        cityCode: data.city,
        isMiscarriage,
        isDifficultBirth: data.deliveryMethod === "difficult",
        numberOfBabies:
          data.birthType === "single"
            ? 1
            : data.birthType === "twins"
              ? 2
              : data.birthType === "triplets"
                ? 3
                : 4,
        expectedDeliveryDate: data.startDate,
        hasExtendedDays: data.hasExtendedDays || false,
        ...(isMiscarriage ? { miscarriageLeaveDetail: computeMiscarriageDetail() } : {}),
        // 绍兴城市特殊参数：是否为一孩
        ...(data.city === "SX" && data.firstChild ? { isFirstTimeBirth: data.firstChild === "yes" } : {}),
      };

      // 调用真实接口
      const apiData = await post<ApiCalculateResponse>(
        "https://www.54cpa.cn/api/maternity-leave/calculate",
        payload,
      );
      const calculatedDays = apiData?.totalDays ?? apiData?.total ?? 0;
      const totalAllowanceDays = apiData?.totalAllowanceDays ?? calculatedDays; // 如果没有单独的津贴天数，使用总天数
      const maternityLeaveStartDate =
        apiData?.startDate ??
        apiData?.maternityStartDate ??
        apiData?.maternityPeriod?.startDate;
      const maternityLeaveEndDate =
        apiData?.endDate ??
        apiData?.maternityEndDate ??
        apiData?.maternityPeriod?.endDate;
      // 接收后端返回的isFirstTimeBirth结果（绍兴城市）
      const isFirstTimeBirth = apiData?.isFirstTimeBirth;
      const updatedData = {
        ...data,
        calculatedDays,
        totalAllowanceDays,
        maternityLeaveStartDate,
        maternityLeaveEndDate,
        ...(typeof isFirstTimeBirth === 'boolean' ? { isFirstTimeBirth } : {}),
      };
      onChange(updatedData);
    } catch (error: unknown) {
      console.error("计算失败:", error);
      const message =
        typeof error === "object" && error && "message" in error
          ? String((error as { message?: unknown }).message)
          : "计算失败，请稍后重试";
      setErrorMessage(message);
    } finally {
      setCalculating(false);
    }
  };

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);

    if (date) {
      // Fix timezone issue by using local date string format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const localDateString = `${year}-${month}-${day}`;
      handleChange("startDate", localDateString);
    } else {
      // 清空日期时，同时清空 startDate
      handleChange("startDate", "");
    }
  };

  return (
    <div className="vacation-section">
      <div
        className="vacation-header"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <h3 className="section-title">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i
              className={`fas ${expanded ? "fa-chevron-down" : "fa-chevron-right"}`}
            ></i>
            <i className="fas fa-calendar-alt"></i>
            假期计算
          </div>

          {data.calculatedDays > 0 && (
            <span className="vacation-status">
              {`${data.calculatedDays}天`}
              {data.employeeName && ` - ${data.employeeName}`}
            </span>
          )}
        </h3>
      </div>

      {expanded && (
        <div className="vacation-content">
          {/* 城市选择 */}
          <div className="form-group">
            <label htmlFor="vacationCity" className="form-label">
              <i className="fas fa-map-marker-alt"></i>
              选择城市
            </label>
            <select
              id="vacationCity"
              className="form-input"
              value={data.city}
              onChange={(e) => handleChange("city", e.target.value)}
              disabled={citiesLoading || cityOptions.length === 0}
              required
            >
              {cityOptions.length === 0 ? (
                <option value="" disabled>
                  加载城市中...
                </option>
              ) : (
                <>
                  <option value="">请选择城市</option>
                  {cityOptions.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </>
              )}
            </select>
            <small className="form-help">不同城市的产假政策可能有所差异</small>
          </div>

          {/* 用户信息 */}
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user" />
              员工姓名
            </label>
            <input
              type="text"
              className="form-input"
              value={data.employeeName}
              onChange={(e) => handleChange("employeeName", e.target.value)}
              placeholder="请输入员工姓名"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-id-card" />
              员工工号
            </label>
            <input
              type="text"
              className={`form-input ${validationErrors.employeeId ? "error" : ""}`}
              value={data.employeeId}
              onChange={(e) => handleChange("employeeId", e.target.value)}
              placeholder="请输入员工工号（如：A5123456）"
              pattern="[Aa]\d{7}"
              maxLength={8}
              required
            />
            {validationErrors.employeeId ? (
              <small className="form-error">
                {validationErrors.employeeId}
              </small>
            ) : (
              <small className="form-help">
                格式：A5开头加6位数字（如：A5123456）
              </small>
            )}
          </div>
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            label="产假开始日期"
            helpText="请选择产假开始日期"
            holidays={holidays}
            defaultCollapsed={!!data.startDate}
          />

<div className="form-group">
            <label className="form-label">
              <i className="fas fa-medical"></i>
              生育方式
            </label>
            <div className="delivery-methods">
              <div
                className={`delivery-section ${data.deliveryMethod === "normal" ? "delivery-normal" : ""}`}
              >
                <label className="radio-label delivery-main">
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="normal"
                    checked={data.deliveryMethod === "normal"}
                    onChange={() => handleChange("deliveryMethod", "normal")}
                  />
                  <span className="radio-mark" />
                  顺产
                </label>
              </div>

              <div
                className={`delivery-section difficult-delivery ${data.deliveryMethod === "difficult" ? "delivery-difficult" : ""}`}
              >
                <label className="radio-label delivery-main">
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="difficult"
                    checked={data.deliveryMethod === "difficult"}
                    onChange={() => handleChange("deliveryMethod", "difficult")}
                  />
                  <span className="radio-mark" />
                  难产
                </label>
              </div>

              <div
                className={`delivery-section ${data.deliveryMethod === "miscarriage" ? "delivery-miscarriage" : ""}`}
              >
                <label className="radio-label delivery-main">
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="miscarriage"
                    checked={data.deliveryMethod === "miscarriage"}
                    onChange={() =>
                      handleChange("deliveryMethod", "miscarriage")
                    }
                  />
                  <span className="radio-mark" />
                  流产
                </label>


                {data.deliveryMethod === "miscarriage" && (
                        <div className="delivery-sub-options">
                          {!data.city ? (
                            <div className="form-help" style={{ color: "var(--warning-color)", marginBottom: "10px" }}>
                              <i className="fas fa-exclamation-triangle" /> 请先选择城市
                            </div>
                          ) : (
                            <>
                              <label className="form-label sub-label">
                                <i className="fas fa-info-circle" /> 流产规则
                              </label>
                              <div className="input-with-button">
                                <select
                                  className="form-input"
                                  value={miscarriageRule}
                                  onChange={(e) => setMiscarriageRule(e.target.value)}
                                  disabled={miscarriageRulesLoading}
                                >
                                  {miscarriageRulesLoading && <option>加载中...</option>}
                                  {!miscarriageRulesLoading && miscarriageRules.length === 0 && (
                                    <option value="">无可用规则</option>
                                  )}
                                  {!miscarriageRulesLoading && miscarriageRules.length > 0 && (
                                    miscarriageRules.map((item, idx) => {
                                      const val = (item['code'] ?? item['id'] ?? item['value'] ?? item['description'] ?? `rule_${idx}`).toString();
                                      const label = (item['description'] ?? item['name'] ?? val).toString();
                                      return (
                                        <option key={val} value={val}>{label}</option>
                                      );
                                    })
                                  )}
                                </select>
                              </div>
                            </>
                          )}
                          {data.city === 'SZ' && miscarriageRule === miscarriageFirstValue && (
                            <div className="form-group" style={{ marginTop: 10 }}>
                              <label className="form-label sub-label">
                                <i className="fas fa-user-md" /> 医生建议休息天数
                              </label>
                              <input
                                type="number"
                                className="form-input w-full"
                                min="0"
                                placeholder="请输入医生建议的休息天数"
                                value={doctorRestDays}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  // 仅允许非负整数
                                  if (/^\d*$/.test(v)) setDoctorRestDays(v);
                                }}
                              />
                            </div>
                          )}
                          <small className="form-help"></small>
                        </div>
                      )}
              </div>
            </div>
          </div>
          
          {/* 胎儿数量 - 流产时隐藏 */}
          {data.deliveryMethod !== "miscarriage" && (
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-baby" />
                几胞胎
              </label>
              <select
                className="form-input"
                value={data.birthType}
                onChange={(e) => {
                  const value = e.target.value as VacationData["birthType"];
                  handleChange("birthType", value);
                }}
                required
              >
                <option value="single">单胎</option>
                <option value="twins">双胞胎</option>
                <option value="triplets">三胞胎</option>
                <option value="quadruplets">四胞胎</option>
              </select>
              <small className="form-help">选择胎儿数量类型</small>
            </div>
          )}

          {/* 晚育/生育/奖励假 - 流产时隐藏 */}
          {data.deliveryMethod !== "miscarriage" && (
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar-plus"></i>
                晚育/生育/奖励假
              </label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={data.hasExtendedDays}
                    onChange={(e) => handleChange("hasExtendedDays", e.target.checked)}
                  />
                  <span className="checkmark" />
                  晚育/生育/奖励假
                </label>
              </div>
            </div>
          )}

          {/* 绍兴特殊选项 - 是否生育一孩 */}
          {data.city === "SX" && data.deliveryMethod !== "miscarriage" && (
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-baby"></i>
                是否生育一孩
              </label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="firstChild"
                    value="yes"
                    checked={data.firstChild === "yes"}
                    onChange={() => handleChange("firstChild", "yes")}
                  />
                  <span className="radio-mark" />
                  是
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="firstChild"
                    value="no"
                    checked={data.firstChild === "no"}
                    onChange={() => handleChange("firstChild", "no")}
                  />
                  <span className="radio-mark" />
                  否
                </label>
              </div>
            </div>
          )}

          {/* 计算结果显示 */}
          {data.calculatedDays > 0 && (
            <div className="form-group">
              <div className="calculated-result">
                <label className="form-label">
                  <i className="fas fa-calculator"></i>
                  享受生育假期天数
                </label>
                <div className="result-display" id="calculatedDays">
                  <span className="result-number">{data.calculatedDays}</span>
                  <span className="result-unit">天</span>
                </div>
                <small className="form-help">
                  根据选择的城市和生育情况自动计算
                </small>
              </div>
            </div>
          )}

          {/* API计算按钮 - 仅在非来自产假计算页面时显示 */}
          {!fromMaternityCalculator && (
            <div className="form-group">
              <button
                type="button"
                onClick={() => {
                  calculateVacationAPI();
                }}
                className="calculate-btn secondary-btn"
                disabled={
                  calculating ||
                  !data.employeeName ||
                  !data.employeeId ||
                  !data.city ||
                  !data.startDate ||
                  !data.birthType ||
                  !data.deliveryMethod
                }
              >
                <i
                  className={
                    calculating ? "fas fa-spinner fa-spin" : "fas fa-calculator"
                  }
                ></i>
                {calculating ? "计算中..." : "计算假期天数"}
              </button>

              {errorMessage && (
                <div
                  className="error-message"
                  role="alert"
                  style={{ color: "var(--error-color)", marginTop: "8px" }}
                >
                  <i
                    className="fas fa-exclamation-triangle"
                    style={{ marginRight: "6px" }}
                  ></i>
                  "请求失败，请稍后再试"
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VacationCalculation;
